/**
 * La requalification en salariat : où se situe votre relation.
 *
 * CET OUTIL NE PRONONCE AUCUN VERDICT, et c'est sa règle fondatrice. La
 * qualification d'un contrat appartient au juge, qui apprécie les conditions de
 * fait dans leur ensemble. Le référentiel range d'ailleurs le verdict parmi les
 * sujets à ne pas chiffrer. Ce module dit lesquels des trois pouvoirs une
 * situation touche, et ce que les juges en font. Rien de plus, et surtout aucun
 * score : un nombre se lirait comme une probabilité.
 *
 * L'IMMATRICULATION JOUE POUR VOUS AU DÉPART. L'article L. 8221-6 du code du
 * travail présume qu'une personne immatriculée comme travailleur indépendant
 * n'est pas liée à son donneur d'ordre par un contrat de travail. C'est une
 * présomption SIMPLE : elle tombe dès qu'un lien de subordination juridique
 * permanente est établi. Mais c'est à celui qui invoque le contrat de travail de
 * la renverser, jamais à l'indépendant de prouver son indépendance.
 *
 * TROIS POUVOIRS, ET EUX SEULS, caractérisent la subordination, depuis l'arrêt
 * de la Cour de cassation du 13 novembre 1996 : donner des ordres et des
 * directives, en contrôler l'exécution, sanctionner les manquements. Tout le
 * reste, horaires, matériel, locaux, nombre de clients, n'est qu'un faisceau
 * d'indices servant à les établir. Un indice isolé ne caractérise rien, et c'est
 * pour cela que le module raisonne par pouvoir et non par indice.
 *
 * AUCUN SEUIL DE CLIENTS N'EXISTE. Ni nombre minimum, ni pourcentage de chiffre
 * d'affaires. Les seuils qui circulent sont inventés. Travailler pour un seul
 * client est licite ; avoir dix clients ne protège de rien si la subordination
 * est caractérisée avec l'un d'eux. Le module ne demande donc jamais combien
 * vous avez de clients, il demande si vous en avez d'autres.
 */

/** D'où viennent les règles, et quand elles ont été relevées. */
export const BAREME = {
    annee: "2026",
    releveLe: "2026-09-08",
    source: "article L. 8221-6 du code du travail sur Légifrance, et le ministère du Travail sur le recours abusif aux travailleurs indépendants",
};

/**
 * Les trois pouvoirs du donneur d'ordre, et les indices qui les établissent.
 *
 * Chaque indice est rattaché au pouvoir qu'il sert à démontrer : c'est ainsi que
 * les juges raisonnent, et c'est ce qui permet de rendre un résultat lisible
 * sans jamais additionner des points.
 */
export const POUVOIRS = [
    {
        cle: "ordres",
        indices: ["horaires", "methode", "tarif"],
    },
    {
        cle: "controle",
        indices: ["rendreCompte", "materiel", "equipe"],
    },
    {
        cle: "sanction",
        indices: ["sanction", "exclusivite"],
    },
];

/** Tous les indices de subordination, dans l'ordre où le parcours les pose. */
export const INDICES = POUVOIRS.flatMap((p) => p.indices);

/**
 * Les marques d'indépendance, qui ne se rangent sous aucun pouvoir.
 *
 * Elles ne compensent pas arithmétiquement les indices de subordination : elles
 * sont ce que l'indépendant peut PROUVER, et c'est ce qui compte devant un juge.
 */
export const MARQUES = ["refuser", "remplacer", "livrable", "clientele", "outils"];

/** Le cas le plus exposé, qui se pose à part. */
export const ANCIEN_EMPLOYEUR = "ancienEmployeur";

/** Une réponse cochée. */
function coche(reponses, cle) {
    return Boolean((reponses || {})[cle]);
}

/**
 * Quels pouvoirs la situation touche, et par quels indices.
 *
 * Rend `null` tant que l'écran des indices n'a pas été traversé : un résultat
 * partiel laisserait croire à une situation plus saine qu'elle n'est.
 */
export function diagnostic(reponses) {
    const r = reponses || {};
    if (!r.repondu) return null;

    const touches = POUVOIRS.map((p) => ({
        cle: p.cle,
        indices: p.indices.filter((i) => coche(r, i)),
    })).filter((p) => p.indices.length > 0);

    const marques = MARQUES.filter((m) => coche(r, m));
    const indices = INDICES.filter((i) => coche(r, i));

    return {
        // Les trois pouvoirs sont le test légal : on dit lesquels sont touchés,
        // jamais « combien de points sur dix ».
        pouvoirsTouches: touches.map((p) => p.cle),
        detail: touches,
        tousLesPouvoirs: touches.length === POUVOIRS.length,
        aucunPouvoir: touches.length === 0,
        indices,
        marques,
        // Facturer les mêmes tâches à son ancien ou actuel employeur est le cas
        // que les juges regardent en premier.
        ancienEmployeur: coche(r, ANCIEN_EMPLOYEUR),
    };
}

/** Ce qui manque pour rendre un résultat. */
export function valider(reponses) {
    const r = reponses || {};
    return {
        valide: Boolean(r.repondu),
        manques: r.repondu ? [] : ["repondu"],
        invalides: [],
        alertes: [],
    };
}

/**
 * Ce que le parcours annonce, ligne par ligne.
 *
 * Aucune ligne ne conclut. Elles décrivent, et la dernière rappelle qui tranche.
 */
export function recapitulatif(reponses) {
    const d = diagnostic(reponses);
    if (!d) return null;

    const nomPouvoir = {
        ordres: "Donner des ordres et des directives",
        controle: "Contrôler l'exécution",
        sanction: "Sanctionner les manquements",
    };

    const lignes = POUVOIRS.map((p) => {
        const touche = d.detail.find((t) => t.cle === p.cle);
        return [
            nomPouvoir[p.cle],
            touche
                ? `${touche.indices.length} indice${touche.indices.length > 1 ? "s" : ""} sur ${p.indices.length} relevé${touche.indices.length > 1 ? "s" : ""} dans vos réponses`
                : "aucun indice dans vos réponses",
        ];
    });

    lignes.push([
        "Ce que vous pourriez prouver",
        d.marques.length
            ? `${d.marques.length} marque${d.marques.length > 1 ? "s" : ""} d'indépendance sur ${MARQUES.length}`
            : "aucune marque d'indépendance dans vos réponses",
    ]);

    if (d.ancienEmployeur) {
        lignes.push(["Votre client", "votre ancien ou actuel employeur, pour des tâches comparables"]);
    }

    lignes.push(["Qui qualifie la relation", "le juge, et lui seul, au vu des conditions de fait"]);
    return lignes;
}
